import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { stringToNumber } from 'helpers/Prices';
import {
  IEstPayFilters,
  IEstPayPageContentData,
  IEstPayPageContentSettings,
  IEstPayResults,
  IEstPayServiceData,
  IEstPaySpecialOffer,
  IEstPayVehicleTrim,
  IReduxUserInputs,
} from 'interfaces/estimatePayments';
import { formatError } from 'helpers/FormatText';
import _ from 'lodash';

export interface IEstPaySlice {
  estimatePaymentShown: 'Finance' | 'Lease';
  openOfferDetail?: string;
  tradeInModalOpen: boolean;
  savedScroll: number;
  savedHeights: {
    legal: number;
    topSection: number;
  };
  rightPanelLoading: boolean;
  financeValues: {
    financeStep: number;
    financeTerms: string[];
  };
  leaseValues: {
    leaseStep: number;
    leaseTerms: string[];
  };
  errors: {
    apr: string;
    amountOwed: string;
    downPayment: string;
    annualMileage: string;
    tradeIn: string;
  };
  pageGeneralData?: {
    settings: IEstPayPageContentSettings;
    vehicleTrims: IEstPayVehicleTrim[];
    labels: { [key: string]: string };
  };
  paymentServiceData?: {
    defaults: {
      defaultNewVehicleAPR: number;
      defaultLeaseTerm: number;
      defaultFinanceTerm: number;
      defaultAnnualMileage: number;
    };
    results: IEstPayResults;
    leaseOffers: IEstPaySpecialOffer[];
    financeOffers: IEstPaySpecialOffer[];
    generalOffers: IEstPaySpecialOffer[];
    filters: IEstPayFilters;
  };
  selectedTrim: IEstPayVehicleTrim;
  userInputs: IReduxUserInputs;
  modelFirstLoad: boolean;
  loadPaymentQuery?: string;
  pageFirstLoad: boolean;
  setBestRateApr: boolean;
}

const emptyInputs: IReduxUserInputs = {
  downPayment: 0,
  financeTerm: 0,
  leaseTerm: 0,
  financeCreditScore: 0,
  leaseCreditScore: 0,
  apr: 0,
  annualMileage: 0,
  tradeInValue: 0,
  amountOwed: 0,
  financeOffers: [],
  leaseOffers: [],
};

const emptyModel: IEstPayVehicleTrim = {
  name: '',
  modelId: '',
  exteriorColorCode: '',
  interiorColorCode: '',
};

const initialState: IEstPaySlice = {
  estimatePaymentShown: 'Lease',
  rightPanelLoading: false,
  tradeInModalOpen: false,
  financeValues: {
    financeStep: 0,
    financeTerms: [],
  },
  leaseValues: {
    leaseStep: 0,
    leaseTerms: [],
  },
  errors: {
    apr: '',
    amountOwed: '',
    downPayment: '',
    annualMileage: '',
    tradeIn: '',
  },
  savedScroll: 0,
  savedHeights: {
    legal: 0,
    topSection: 0,
  },
  pageGeneralData: undefined,
  selectedTrim: emptyModel,
  userInputs: emptyInputs,
  modelFirstLoad: true,
  pageFirstLoad: true,
  setBestRateApr: true,
};

export const estimatePaymentsSlice = createSlice({
  name: 'estimatePayments',
  initialState,
  reducers: {
    setErrorData: (state, action: PayloadAction<string>) => {
      state.rightPanelLoading = false;
      const errorMessage = action.payload.toLowerCase();
      const aprErrors = [
        'Validation.Finance.Input.APR',
        'mit.input.Required.APR',
      ];
      const downPaymentErrors = [
        'Validation.Common.Input.DownPaymentTradeInValue',
        'Validation.Calculator.DownPaymentTradeInValue.OutOfRange',
        'Validation.Lease.Total.TooLow',
        'mit.input.Optional.downPayment',
      ];
      const amountOwedErrors = [
        'Validation.Finance.Business.FinancedAmount',
        'mit.input.Optional.BalanceOwed',
      ];
      const annualMileageErrors = [
        'Validation.Finance.Input.Mileage',
        'Validation.Finance.Input.Mileage.InvalidForTerm',
      ];
      const tradeInErrors = [
        'mit.input.Optional.TradeInValue',
        'Validation.Common.Input.DownPaymentTradeInValue',
        'Validation.Calculator.DownPaymentTradeInValue.OutOfRange',
        'Validation.Lease.Total.TooLow',
      ];
      if (aprErrors.some((v) => errorMessage.includes(v.toLowerCase()))) {
        state.errors.apr =
          state.pageGeneralData?.labels.annualPercentageRateError || '';
      }
      if (
        downPaymentErrors.some((v) => errorMessage.includes(v.toLowerCase()))
      ) {
        state.errors.downPayment =
          formatError(action.payload) ||
          'Cap cost reduction exceeds 40.00% of cap cost.';
      }
      if (
        amountOwedErrors.some((v) => errorMessage.includes(v.toLowerCase()))
      ) {
        state.errors.amountOwed =
          state.pageGeneralData?.labels.amountOwedError || '';
      }
      if (
        annualMileageErrors.some((v) => errorMessage.includes(v.toLowerCase()))
      ) {
        state.errors.annualMileage =
          state.pageGeneralData?.labels.mileageError || '';
      }
      if (tradeInErrors.some((v) => errorMessage.includes(v.toLowerCase()))) {
        state.errors.tradeIn =
          state.pageGeneralData?.labels.lowerAmountError || '';
      }
    },
    setRightPanelLoading: (state, action: PayloadAction<boolean>) => {
      state.rightPanelLoading = action.payload;
    },
    setSelectedVehicleTrim: (
      state,
      action: PayloadAction<IEstPayVehicleTrim | undefined>
    ) => {
      if (action.payload) {
        state.rightPanelLoading = true;
        state.selectedTrim = action.payload;
        state.modelFirstLoad = true;
        state.setBestRateApr = true;
      }
    },
    setPaymentQuery: (state, action: PayloadAction<string>) => {
      state.loadPaymentQuery = action.payload;
    },
    setTopHeight: (state, action: PayloadAction<number>) => {
      state.savedHeights.topSection = action.payload;
    },
    setLegalHeight: (state, action: PayloadAction<number>) => {
      state.savedHeights.legal = action.payload;
    },
    openTradeInModal: (state) => {
      state.tradeInModalOpen = true;
    },
    closeTradeInModal: (state) => {
      state.tradeInModalOpen = false;
    },
    openOfferDetail: (
      state,
      action: PayloadAction<{ id: string; scroll?: number }>
    ) => {
      state.openOfferDetail = action.payload.id;
      state.savedScroll = action.payload.scroll || 0;
    },
    closeOfferDetail: (state) => {
      state.openOfferDetail = undefined;
    },
    setEstimatePaymentShown: (
      state,
      action: PayloadAction<'Finance' | 'Lease'>
    ) => {
      state.estimatePaymentShown = action.payload;
    },
    setDownPayment: (state, action: PayloadAction<number>) => {
      state.rightPanelLoading = true;
      state.userInputs.downPayment = action.payload;
    },
    setApr: (state, action: PayloadAction<number>) => {
      state.rightPanelLoading = true;
      state.userInputs.financeCreditScore = 900;

      const fullOffer = state.paymentServiceData?.financeOffers.find((x) => {
        return state.userInputs.financeOffers.includes(x.eShoppingId);
      });
      const selectedOfferTerm = fullOffer?.financePaymentInfo?.termRanges.find(
        (term) => {
          return term.apr === action.payload;
        }
      );

      // validate if new apr value is accepted for selected offer
      if (!selectedOfferTerm) {
        state.userInputs.apr = action.payload;
      } else {
        // loop over constants and select highest value between term max and term min
        const termsAvailable = (
          state.paymentServiceData?.filters.financeTerms || []
        ).map((x) => x);
        const newTerm = termsAvailable.reverse().find((term) => {
          const t = parseFloat(term);

          return t >= selectedOfferTerm.min && t <= selectedOfferTerm.max;
        });

        if (newTerm) {
          state.userInputs.financeTerm = +newTerm;
        } else {
          state.userInputs.financeTerm =
            +termsAvailable[termsAvailable.length - 1];
        }

        state.userInputs.apr = selectedOfferTerm.apr;
      }
    },
    setSelectedAnnualMileage: (state, action: PayloadAction<number>) => {
      state.rightPanelLoading = true;
      state.userInputs.annualMileage = action.payload;
    },
    setFinanceTerm: (state, action: PayloadAction<number>) => {
      state.rightPanelLoading = true;
      const fullOffer = state.paymentServiceData?.financeOffers.find((x) => {
        return state.userInputs.financeOffers.includes(x.eShoppingId);
      });
      const selectedOfferTerm = fullOffer?.financePaymentInfo?.termRanges.find(
        (t) => action.payload <= t.max && action.payload >= t.min
      );

      // validate if new term matches apr, set apr to new offer if not
      if (selectedOfferTerm && !fullOffer?.ficoDisabled) {
        state.userInputs.apr = selectedOfferTerm.apr;
      } else {
        state.setBestRateApr = true;
      }
      state.userInputs.financeTerm = action.payload;
    },
    setLeaseTerm: (state, action: PayloadAction<number>) => {
      state.rightPanelLoading = true;
      state.userInputs.leaseTerm = action.payload;
    },
    setFinanceCreditScore: (state, action: PayloadAction<number>) => {
      state.rightPanelLoading = true;
      state.userInputs.financeCreditScore = action.payload;
      state.setBestRateApr = true;
    },
    setLeaseCreditScore: (state, action: PayloadAction<number>) => {
      state.rightPanelLoading = true;
      state.userInputs.leaseCreditScore = action.payload;
    },
    setTradeInValue: (state, action: PayloadAction<number>) => {
      state.rightPanelLoading = true;
      state.userInputs.tradeInValue = action.payload;
    },
    setAmountOwed: (state, action: PayloadAction<number>) => {
      state.rightPanelLoading = true;
      state.userInputs.amountOwed = action.payload;
    },
    addOffer: (state, action: PayloadAction<string>) => {
      state.rightPanelLoading = true;
      let offersToRemove: string[] = [];
      const selected = state.paymentServiceData?.results.specialOffers.find(
        (x) => {
          return x.eShoppingId === action.payload;
        }
      );
      const currentSelectedIds =
        state.estimatePaymentShown === 'Lease'
          ? state.userInputs.leaseOffers
          : state.userInputs.financeOffers;
      if (selected?.allowedStackableOffers) {
        const specialOfferIds =
          state.paymentServiceData?.results.specialOffers.map(
            (specialOffer) => specialOffer.eShoppingId
          );
        if (selected.allowedStackableOffers.length === 0) {
          const offersNotAllowed = _.without(specialOfferIds, action.payload);

          offersToRemove = offersNotAllowed;
        } else {
          const offersNotAllowed = _.difference(
            specialOfferIds,
            selected.allowedStackableOffers
          );
          offersToRemove = _.intersection(offersNotAllowed, currentSelectedIds);
        }
      }
      if (state.estimatePaymentShown === 'Lease') {
        state.userInputs.leaseOffers.push(action.payload);
        const fullOffer = state.paymentServiceData?.leaseOffers.find((x) => {
          return x.eShoppingId === action.payload;
        });
        if (fullOffer && fullOffer.leasePaymentInfo) {
          state.userInputs.downPayment =
            fullOffer.leasePaymentInfo.capitalCostReduction || 0;

          const term = fullOffer.leasePaymentInfo.termMonths
            ? fullOffer.leasePaymentInfo.termMonths
            : state.paymentServiceData?.defaults.defaultLeaseTerm || 36;

          state.userInputs.leaseTerm = fullOffer.leasePaymentInfo.termRanges
            ? fullOffer.leasePaymentInfo.termRanges[0].max
            : term;
        }
        let selectedOfferTerm: number | undefined;
        if (fullOffer?.leasePaymentInfo?.termRanges) {
          selectedOfferTerm = fullOffer.leasePaymentInfo.termRanges[0].max;
        } else if (fullOffer?.leasePaymentInfo?.termMonths) {
          selectedOfferTerm = fullOffer.leasePaymentInfo.termMonths;
        }
        if (selectedOfferTerm) {
          state.leaseValues.leaseTerms = [`${selectedOfferTerm}`];
        }
        if (offersToRemove) {
          offersToRemove.forEach((offerIdToRemove) => {
            if (currentSelectedIds.includes(offerIdToRemove)) {
              state.userInputs.leaseOffers = _.without(
                state.userInputs.leaseOffers,
                offerIdToRemove
              );
            }
          });
        }
      } else {
        state.userInputs.financeOffers.push(action.payload);
        const fullOffer = state.paymentServiceData?.financeOffers.find((x) => {
          return x.eShoppingId === action.payload;
        });
        if (fullOffer && fullOffer.financePaymentInfo) {
          const terms = fullOffer.financePaymentInfo.termRanges[0];
          const termsAvailable = (
            state.paymentServiceData?.filters.financeTerms || []
          ).map((x) => x);
          const newTerm = termsAvailable.reverse().find((term) => {
            const t = parseFloat(term);

            return t >= terms.min && t <= terms.max;
          });

          if (newTerm) {
            state.userInputs.financeTerm = +newTerm;
          } else {
            state.userInputs.financeTerm = +termsAvailable[0];
          }

          state.userInputs.apr = terms.apr;
          const financeTerm = fullOffer?.financePaymentInfo.termRanges || [];

          const min =
            Math.min.apply(
              null,
              financeTerm.map((t) => t.min)
            ) || 0;
          const max =
            Math.max.apply(
              null,
              financeTerm.map((t) => t.max)
            ) || 0;
          state.financeValues.financeTerms = (
            state.paymentServiceData?.filters.financeTerms || []
          ).filter((value) => +value >= min && +value <= max);
        }
        if (offersToRemove) {
          offersToRemove.forEach((offerIdToRemove) => {
            if (currentSelectedIds.includes(offerIdToRemove)) {
              const offerToRemove = _.find(
                state.paymentServiceData?.results.specialOffers,
                (o) => o.eShoppingId === offerIdToRemove
              );

              if (offerToRemove && offerToRemove.type !== 'Finance') {
                state.userInputs.financeOffers = _.without(
                  state.userInputs.financeOffers,
                  offerIdToRemove
                );
              }
            }
          });
        }
      }
    },
    removeOffer: (state, action: PayloadAction<string>) => {
      state.rightPanelLoading = true;
      const fullOffer = state.paymentServiceData?.results.specialOffers.find(
        (x) => {
          return x.eShoppingId === action.payload;
        }
      );
      if (state.estimatePaymentShown === 'Lease') {
        state.userInputs.leaseOffers = _.without(
          state.userInputs.leaseOffers,
          action.payload
        );
        if (fullOffer?.type === 'Lease') {
          state.leaseValues.leaseTerms =
            state.paymentServiceData?.filters.leaseTerms || [];
        }
      } else {
        state.userInputs.financeOffers = _.without(
          state.userInputs.financeOffers,
          action.payload
        );
        if (fullOffer?.type === 'Finance') {
          state.financeValues.financeTerms =
            state.paymentServiceData?.filters.financeTerms || [];
          state.setBestRateApr = true;
        }
      }
    },
    setPageContentData: (
      state,
      action: PayloadAction<{ data: IEstPayPageContentData; modelId: string }>
    ) => {
      const defaultVehicleTrim = action.payload.data.trims.find(
        (x) =>
          x.modelId ===
          (action.payload.modelId === ''
            ? action.payload.data.settings.defaultTrim
            : action.payload.modelId)
      );

      if (defaultVehicleTrim && state.selectedTrim.modelId === '') {
        state.selectedTrim = defaultVehicleTrim;
      }
      state.pageGeneralData = {
        settings: action.payload.data.settings,
        vehicleTrims: action.payload.data.trims,
        labels: action.payload.data.pageContent.labels,
      };
    },
    setPaymentServiceData: (
      state,
      action: PayloadAction<IEstPayServiceData>
    ) => {
      state.errors = {
        apr: '',
        amountOwed: '',
        annualMileage: '',
        downPayment: '',
        tradeIn: '',
      };
      const ficoList = action.payload.pageContent.filters.ficoScores;
      state.rightPanelLoading = false;
      if (state.pageFirstLoad) {
        state.leaseValues.leaseTerms =
          action.payload.pageContent.filters.leaseTerms;
        state.financeValues.financeTerms =
          action.payload.pageContent.filters.financeTerms;
        if (state.loadPaymentQuery) {
          const decodedQuery = atob(state.loadPaymentQuery);
          const parameters = decodedQuery.split('$').reduce((acc, cur) => {
            const split = cur.split(':');

            if (split.length === 2) {
              acc[split[0]] = split[1];
            }

            return acc;
          }, {} as { [key: string]: string });
          const config = {
            trim: parameters['M'] || undefined,
            termOfFinance: parameters['FT'] ? +parameters['FT'] : undefined,
            termOfLease: parameters['LT'] ? +parameters['LT'] : undefined,
            annualMileage: parameters['MILE'] ? +parameters['MILE'] : undefined,
            downPayment: parameters['DP'] ? +parameters['DP'] : undefined,
            tradeInValue: parameters['TIV'] ? +parameters['TIV'] : undefined,
            apr: parameters['APR'] ? +parameters['APR'] : undefined,
            balanceOwed: parameters['AO'] ? +parameters['AO'] : undefined,
            leaseOfferIds: parameters['OL'] || undefined,
            financeOfferIds: parameters['OF'] || undefined,
            estimatedCreditLease:
              parameters['SL'] || parameters['S'] || undefined,
            estimatedCreditFinance:
              parameters['SF'] || parameters['S'] || undefined,
          };
          const configVehicleTrim = state.pageGeneralData?.vehicleTrims.find(
            (x) => x.modelId === config.trim || ''
          );
          if (configVehicleTrim) state.selectedTrim = configVehicleTrim;
          state.userInputs = {
            downPayment: config.downPayment || 0,
            financeTerm:
              config.termOfFinance ||
              action.payload.settings.defaultFinanceTerm,
            leaseTerm:
              config.termOfLease || action.payload.settings.defaultLeaseTerm,
            financeCreditScore: stringToNumber(
              config.estimatedCreditFinance ||
                (ficoList.length !== 0 ? ficoList[0].value : '0')
            ),
            leaseCreditScore: stringToNumber(
              config.estimatedCreditLease ||
                (ficoList.length !== 0 ? ficoList[0].value : '0')
            ),
            apr: config.apr || action.payload.settings.defaultNewVehicleAPR,
            annualMileage:
              config.annualMileage ||
              action.payload.settings.defaultAnnualMileage,
            tradeInValue: config.tradeInValue || 0,
            amountOwed: config.balanceOwed || 0,
            financeOffers: config.financeOfferIds?.split(',') || [],
            leaseOffers: config.leaseOfferIds?.split(',') || [],
          };
          if (state.userInputs.leaseOffers.length !== 0) {
            const fullOffer = action.payload.paymentResults.specialOffers.find(
              (x) => {
                return (
                  x.type.toLowerCase() === 'lease' &&
                  state.userInputs.leaseOffers.includes(x.eShoppingId)
                );
              }
            );
            let selectedOfferTerm: number | undefined;
            if (fullOffer && fullOffer.leasePaymentInfo) {
              if (fullOffer?.leasePaymentInfo?.termRanges) {
                selectedOfferTerm =
                  fullOffer.leasePaymentInfo.termRanges[0].max;
              } else if (fullOffer?.leasePaymentInfo?.termMonths) {
                selectedOfferTerm = fullOffer.leasePaymentInfo.termMonths;
              }
              if (selectedOfferTerm) {
                state.leaseValues.leaseTerms = [`${selectedOfferTerm}`];
              }
            } else {
              state.userInputs.leaseOffers = [
                action.payload.paymentResults.specialOffers.find((x) => {
                  return x.type.toLowerCase() === 'lease';
                })?.eShoppingId || '',
              ];
            }
          }
          if (state.userInputs.financeOffers.length !== 0) {
            const fullOffer = action.payload.paymentResults.specialOffers.find(
              (x) => {
                return (
                  x.type.toLowerCase() === 'finance' &&
                  state.userInputs.financeOffers.includes(x.eShoppingId)
                );
              }
            );
            if (fullOffer && fullOffer && fullOffer.financePaymentInfo) {
              const financeTerm =
                fullOffer?.financePaymentInfo.termRanges || [];

              const min =
                Math.min.apply(
                  null,
                  financeTerm.map((t) => t.min)
                ) || 0;
              const max =
                Math.max.apply(
                  null,
                  financeTerm.map((t) => t.max)
                ) || 0;
              state.financeValues.financeTerms =
                action.payload.pageContent.filters.financeTerms.filter(
                  (value) => +value >= min && +value <= max
                );
            }
          }
          state.modelFirstLoad = false;
          state.loadPaymentQuery = undefined;
        } else {
          state.userInputs = {
            downPayment: 0,
            financeTerm: action.payload.settings.defaultFinanceTerm,
            leaseTerm: action.payload.settings.defaultLeaseTerm,
            financeCreditScore:
              ficoList.length !== 0 ? stringToNumber(ficoList[0].value) : 0,
            leaseCreditScore:
              ficoList.length !== 0 ? stringToNumber(ficoList[0].value) : 0,
            apr: action.payload.settings.defaultNewVehicleAPR,
            annualMileage: action.payload.settings.defaultAnnualMileage,
            tradeInValue: 0,
            amountOwed: 0,
            financeOffers: [],
            leaseOffers: [],
          };
          state.rightPanelLoading = true;
        }
      } else if (state.modelFirstLoad) {
        state.leaseValues.leaseTerms =
          action.payload.pageContent.filters.leaseTerms;
        state.financeValues.financeTerms =
          action.payload.pageContent.filters.financeTerms;
        state.userInputs = {
          downPayment: 0,
          financeTerm: action.payload.settings.defaultFinanceTerm,
          leaseTerm: action.payload.settings.defaultLeaseTerm,
          financeCreditScore:
            ficoList.length !== 0 ? stringToNumber(ficoList[0].value) : 0,
          leaseCreditScore:
            ficoList.length !== 0 ? stringToNumber(ficoList[0].value) : 0,
          apr: action.payload.settings.defaultNewVehicleAPR,
          annualMileage: action.payload.settings.defaultAnnualMileage,
          tradeInValue: 0,
          amountOwed: 0,
          financeOffers:
            action.payload.paymentResults.financeResults.specialOffers.map(
              (element) => {
                return element.eShoppingId;
              }
            ),
          leaseOffers: [
            action.payload.paymentResults.specialOffers.find((x) => {
              return x.type.toLowerCase() === 'lease';
            })?.eShoppingId || '',
          ],
        };
        if (state.userInputs.leaseOffers.length !== 0) {
          const fullOffer = action.payload.paymentResults.specialOffers.find(
            (x) => {
              return (
                x.type.toLowerCase() === 'lease' &&
                state.userInputs.leaseOffers.includes(x.eShoppingId)
              );
            }
          );
          if (fullOffer && fullOffer.leasePaymentInfo) {
            state.userInputs.downPayment =
              fullOffer.leasePaymentInfo.capitalCostReduction || 0;

            const term = fullOffer.leasePaymentInfo.termMonths
              ? fullOffer.leasePaymentInfo.termMonths
              : action.payload.settings.defaultLeaseTerm;

            state.userInputs.leaseTerm = fullOffer.leasePaymentInfo.termRanges
              ? fullOffer.leasePaymentInfo.termRanges[0].max
              : term;
          }
          let selectedOfferTerm: number | undefined;
          if (fullOffer?.leasePaymentInfo?.termRanges) {
            selectedOfferTerm = fullOffer.leasePaymentInfo.termRanges[0].max;
          } else if (fullOffer?.leasePaymentInfo?.termMonths) {
            selectedOfferTerm = fullOffer.leasePaymentInfo.termMonths;
          }
          if (selectedOfferTerm) {
            state.leaseValues.leaseTerms = [`${selectedOfferTerm}`];
          }
        }
        if (state.userInputs.financeOffers.length !== 0) {
          const fullOffer = action.payload.paymentResults.specialOffers.find(
            (x) => {
              return (
                x.type.toLowerCase() === 'finance' &&
                state.userInputs.financeOffers.includes(x.eShoppingId)
              );
            }
          );
          if (fullOffer && fullOffer.financePaymentInfo) {
            const terms = fullOffer.financePaymentInfo.termRanges[0];
            const termsAvailable =
              action.payload.pageContent.filters.financeTerms.map((x) => x);
            const newTerm = termsAvailable.reverse().find((term) => {
              const t = parseFloat(term);

              return t >= terms.min && t <= terms.max;
            });

            if (newTerm) {
              state.userInputs.financeTerm = +newTerm;
            } else {
              state.userInputs.financeTerm = +termsAvailable[0];
            }

            state.userInputs.apr = terms.apr;
            state.setBestRateApr = false;
            const financeTerm = fullOffer?.financePaymentInfo.termRanges || [];

            const min =
              Math.min.apply(
                null,
                financeTerm.map((t) => t.min)
              ) || 0;
            const max =
              Math.max.apply(
                null,
                financeTerm.map((t) => t.max)
              ) || 0;
            state.financeValues.financeTerms =
              action.payload.pageContent.filters.financeTerms.filter(
                (value) => +value >= min && +value <= max
              );
          }
        }
        state.modelFirstLoad = false;
      }
      if (state.setBestRateApr && !state.pageFirstLoad) {
        if (action.payload.paymentResults.financeResults.bestRate) {
          state.userInputs.apr =
            action.payload.paymentResults.financeResults.bestRate.apr;
        }
        state.setBestRateApr = false;
      }
      const fullOffer = action.payload.paymentResults.specialOffers.find(
        (x) => {
          return (
            x.type.toLowerCase() === 'finance' &&
            state.userInputs.financeOffers.includes(x.eShoppingId)
          );
        }
      );
      if (
        fullOffer &&
        !fullOffer.ficoDisabled &&
        fullOffer.financePaymentInfo
      ) {
        const terms = fullOffer.financePaymentInfo.termRanges.find(
          (t) =>
            state.userInputs.financeTerm <= t.max &&
            state.userInputs.financeTerm >= t.min
        );
        if (terms) {
          state.userInputs.apr = terms.apr;
        }
      }
      state.pageFirstLoad = false;
      var leaseOffers: IEstPaySpecialOffer[] = [];
      var financeOffers: IEstPaySpecialOffer[] = [];
      var generalOffers: IEstPaySpecialOffer[] = [];
      action.payload.paymentResults.specialOffers.forEach(function (
        offer: IEstPaySpecialOffer
      ) {
        if (offer.type === 'Finance') {
          financeOffers.push(offer);
        } else if (offer.type === 'Lease') {
          leaseOffers.push(offer);
        } else {
          generalOffers.push(offer);
        }
      });
      state.paymentServiceData = {
        defaults: { ...action.payload.settings },
        results: { ...action.payload.paymentResults },
        leaseOffers: leaseOffers,
        financeOffers: financeOffers,
        generalOffers: generalOffers,
        filters: action.payload.pageContent.filters,
      };
    },
    setFinanceStep: (state, action: PayloadAction<number>) => {
      state.financeValues.financeStep = action.payload;
    },
    setLeaseStep: (state, action: PayloadAction<number>) => {
      state.leaseValues.leaseStep = action.payload;
    },
  },
});

export const {
  setErrorData,
  setRightPanelLoading,
  setPaymentQuery,
  openTradeInModal,
  closeTradeInModal,
  setTopHeight,
  setLegalHeight,
  setSelectedVehicleTrim,
  setDownPayment,
  setApr,
  setSelectedAnnualMileage,
  setFinanceTerm,
  setLeaseTerm,
  setFinanceCreditScore,
  setLeaseCreditScore,
  setTradeInValue,
  setAmountOwed,
  addOffer,
  removeOffer,
  setPageContentData,
  setPaymentServiceData,
  setEstimatePaymentShown,
  setLeaseStep,
  setFinanceStep,
  openOfferDetail,
  closeOfferDetail,
} = estimatePaymentsSlice.actions;
export default estimatePaymentsSlice.reducer;
